import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip,
  Alert,
  AlertTitle
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

interface OutputDisplayProps {
  output: string;
  isLoading: boolean;
  error?: string;
  onCopy?: () => void;
  onDownload?: () => void;
}

const OutputContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  fontSize: '14px',
  lineHeight: '1.5',
  color: '#333',
  position: 'relative',
  minHeight: '300px',
  '& pre': {
    margin: 0,
    padding: '16px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

const OutputDisplay: React.FC<OutputDisplayProps> = ({
  output,
  isLoading,
  error,
  onCopy,
  onDownload,
}) => {
  const handleCopy = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      onCopy?.();
    }
  };

  const handleDownload = () => {
    if (output) {
      const blob = new Blob([output], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'compiled-output.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onDownload?.();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
          Output
        </Typography>
        <OutputContainer>
          <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
            <Typography>Compiling...</Typography>
          </Box>
        </OutputContainer>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
          Output
        </Typography>
        <OutputContainer>
          <Box sx={{ p: 2, color: '#d32f2f', fontFamily: 'Consolas, Monaco, "Courier New", monospace' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Compilation Error:
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Box>
        </OutputContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 500 }}>
          Output
        </Typography>
        {output && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={handleCopy} sx={{ color: '#666' }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onDownload != null && (
              <Tooltip title="Download output">
                <IconButton size="small" onClick={handleDownload} sx={{ color: '#666' }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
      <OutputContainer>
        {output ? (
          <pre>{output}</pre>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center', color: '#999' }}>
            <Typography variant="body2">No output to display</Typography>
          </Box>
        )}
      </OutputContainer>
    </Box>
  );
};

export default OutputDisplay; 